'use client'

import type { HomePageListing as HomePageListingProps } from 'src/payload-types'
import { cn } from '@/utilities/ui'
import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { Media } from '@/components/Media'
import { ArrowUpRight, BedSingle, Camera, MoveLeft, MoveRight, MoveUpRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatPriceDZD } from '@/utilities/PriceFormat'
import { useTheme } from '@/providers/Theme'
import Image from 'next/image'

type Props = {
  className?: string
  dictionary?: any
  lang?: string
} & HomePageListingProps

// Property Card Component
const PropertyCard = ({ property, dictionary, lang }) => {
  const { theme } = useTheme()

  // Get the first image from the property's images array
  const propertyImage =
    property.mediaSection && property.mediaSection?.images[0]?.image?.url
      ? property.mediaSection?.images[0].image.url
      : '/dummyPhotos/property-placeholder.png'

  // Format the location
  const location =
    property.wilaya && property.city
      ? `${property.city}, ${property.wilaya}`
      : property.wilaya ||
        property.locationSection?.neighborhood ||
        dictionary?.propertyDetail?.homePageListing?.locationNotSpecified ||
        'Location not specified'

  // Get the property's size from the surface field
  const size = property.surface
    ? `${property.surface} m²`
    : property.detailSection?.surface
      ? `${property.detailSection.surface} m²`
      : '—'

  // Get the property's bedrooms from the pieces field
  const bedrooms = property.rooms || property.detailSection?.rooms || '—'

  // Get the property's afterPriceLabel
  const afterPrice = property.afterPriceLabel || property.propertyDescription?.afterPriceLabel

  return (
    <Link
      className=""
      href={lang ? `/${lang}/properties/${property.slug}` : `/properties/${property.slug}`}
    >
      <div className="rounded-lg overflow-hidden bg-card border">
        {/* Property Image */}
        <div className="relative">
          <div className="aspect-[4/3] w-full relative">
            <Image
              src={propertyImage}
              alt={property.title}
              fill
              sizes="(max-width: 640px) 350px, (max-width: 1024px) 400px, 450px"
              quality={85} 
            />
          </div>

          {/* Price Badge */}
          <div className="absolute top-3 left-3 bg-white py-1 px-3 rounded-md font-semibold text-sm">
            {formatPriceDZD(property.propertyDescription?.price, lang)} {afterPrice && ` / ${afterPrice}`}
          </div>
        </div>

        {/* Property Info */}
        <div className="p-4">
          <div className="w-full flex justify-between border-b border-b-border pb-2">
            <div className="flex flex-col gap-2 overflow-hidden">
              <h3 className="text-typography text-lg font-medium truncate max-w-[200px]">{property.title}</h3>
            </div>

            {/* Features */}
            <div className="flex flex-col gap-2">
              <div className="text-typography flex items-center gap-2">
                <span className="inline-block">
                  <BedSingle size={16} />
                </span>
                <span>{bedrooms}</span>
              </div>
              <div className="text-typography flex items-center gap-2">
                {theme === 'dark' ? (
                  <img src="/icons/expand-w.svg" alt="w expand" />
                ) : (
                  <img src="/icons/expand.svg" alt="expand" />
                )}
                <span className="text-sm">{size}</span>
              </div>
            </div>
          </div>

          {/* Views & Link */}
          <div className="flex justify-between items-center mt-4 h-[35px]">
            <div className="h-full flex items-center text-sm text-gray-700 bg-[#F7F7F7] px-2 py-2 rounded-md gap-1">
              <span className="ml-1">{property.mediaSection?.images.length || 0}</span>
              <Camera size={16} />
            </div>

            <Button
              asChild
              variant={'outline'}
              className="!h-[35px] !w-[35px] hover:bg-gray-200 transition-colors border !border-border !p-0"
            >
              <Link
                className="!w-[14px] !h-[14px]"
                href={
                  lang ? `/${lang}/properties/${property.slug}` : `/properties/${property.slug}`
                }
              >
                <img src="/icons/fullscreen.svg" alt="fullscreen" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Link>
  )
}

export const HomePageListing = ({
  className,
  rentalProperties = [],
  saleProperties = [],
  dictionary,
  lang,
}: Props) => {
  const [isMobile, setIsMobile] = useState(false)
  const [rentalActiveSlide, setRentalActiveSlide] = useState(0)
  const [saleActiveSlide, setSaleActiveSlide] = useState(0)
  const rentalSliderRef = useRef(null)
  const saleSliderRef = useRef(null)

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)

    return () => {
      window.removeEventListener('resize', checkIsMobile)
    }
  }, [])

  // Always show 4 dots and make them functional
  const renderRentalDots = () => {
    // We'll always display 4 dots for visual consistency
    const dots = [
      { index: 0, label: dictionary?.propertyDetail?.homePageListing?.firstSlide || 'First slide' },
      {
        index: 1,
        label: dictionary?.propertyDetail?.homePageListing?.secondSlide || 'Second slide',
      },
      { index: 2, label: dictionary?.propertyDetail?.homePageListing?.thirdSlide || 'Third slide' },
      {
        index: 3,
        label: dictionary?.propertyDetail?.homePageListing?.fourthSlide || 'Fourth slide',
      },
    ]

    return (
      <>
        {dots.map((dot, index) => (
          <div
            key={`rental-dot-${index}`}
            className={`w-2 h-2 rounded-full cursor-pointer ${index === rentalActiveSlide ? 'bg-dotsColor' : 'bg-gray-300'}`}
            onClick={() => {
              if (rentalSliderRef.current) {
                rentalSliderRef.current.slickGoTo(index)
                setRentalActiveSlide(index)
              }
            }}
            role="button"
            aria-label={dot.label}
          />
        ))}
      </>
    )
  }

  // Always show 4 dots and make them functional
  const renderSaleDots = () => {
    // We'll always display 4 dots for visual consistency
    const dots = [
      { index: 0, label: dictionary?.propertyDetail?.homePageListing?.firstSlide || 'First slide' },
      {
        index: 1,
        label: dictionary?.propertyDetail?.homePageListing?.secondSlide || 'Second slide',
      },
      { index: 2, label: dictionary?.propertyDetail?.homePageListing?.thirdSlide || 'Third slide' },
      {
        index: 3,
        label: dictionary?.propertyDetail?.homePageListing?.fourthSlide || 'Fourth slide',
      },
    ]

    return (
      <>
        {dots.map((dot, index) => (
          <div
            key={`sale-dot-${index}`}
            className={`w-2 h-2 rounded-full cursor-pointer ${index === saleActiveSlide ? 'bg-dotsColor' : 'bg-gray-300'}`}
            onClick={() => {
              if (saleSliderRef.current) {
                saleSliderRef.current.slickGoTo(index)
                setSaleActiveSlide(index)
              }
            }}
            role="button"
            aria-label={dot.label}
          />
        ))}
      </>
    )
  }

  const rentalSliderSettings = {
    dots: false, // Hide default dots
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    beforeChange: (current, next) => {
      // Only update if navigating to a new section
      const newIndex = Math.min(Math.floor(next / 1), 3)
      setRentalActiveSlide(newIndex)
    },
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  }

  const saleSliderSettings = {
    dots: false, // Hide default dots
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    beforeChange: (current, next) => {
      // Only update if navigating to a new section
      const newIndex = Math.min(Math.floor(next / 1), 3)
      setSaleActiveSlide(newIndex)
    },
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  }

  // Check if we have properties to display
  const hasRentalProperties = Array.isArray(rentalProperties) && rentalProperties.length > 0
  const hasSaleProperties = Array.isArray(saleProperties) && saleProperties.length > 0

  // If there are no properties to display, don't render the component
  if (!hasRentalProperties && !hasSaleProperties) {
    return null
  }

  return (
    <div className={cn('pt-5 bg-light', className)}>
      {/* Rental Properties Section */}
      {hasRentalProperties && (
        <div className="container mx-auto max-md:px-4 mb-5">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-typography sm:text-h2 text-mobile-h2 font-semibold">
                {dictionary?.propertyDetail?.homePageListing?.propertiesForRent ||
                  'Properties For Rent'}
              </h2>
              <p className="text-typography">
                {dictionary?.propertyDetail?.homePageListing?.browseRentalHomes ||
                  'Browse Rental Homes That Match Your Needs'}
              </p>
            </div>
            <div className="hidden lg:flex space-x-5 rtl:space-x-reverse">
              <button
                className="text-typography"
                onClick={() => rentalSliderRef.current?.slickPrev()}
              >
                <MoveLeft size={20} className="rtl:rotate-180" />
              </button>
              <div className="flex items-center space-x-1">{renderRentalDots()}</div>
              <button
                className="text-typography"
                onClick={() => rentalSliderRef.current?.slickNext()}
              >
                <MoveRight size={20} className="rtl:rotate-180" />
              </button>
            </div>
          </div>

          <Slider {...rentalSliderSettings} className="property-slider" ref={rentalSliderRef}>
            {rentalProperties.map((property) => (
              <div key={property.id} className="px-2">
                <PropertyCard property={property} dictionary={dictionary} lang={lang} />
              </div>
            ))}
          </Slider>
          
          {/* BUTTON VIEW ALL RENTED PROPERTIES */}
          <div className="flex justify-center my-5">
            <Link
              href={'/properties?listedIn=for_rent&page=1&sorting=createdAt%3Aasc'}
              className="inline-flex items-center gap-2 h-[55px] px-6 bg-primary-300 text-white rounded-xl hover:bg-primary-200 transition-colors"
            >
              {dictionary?.propertyDetail?.agencyDetail?.seeRentalProperties || 'See Rental Properties'}
              <MoveUpRight />
            </Link>
          </div>
          
          <div className="lg:hidden my-5 flex space-x-5 justify-center">
            <button
              className="text-typography"
              onClick={() => rentalSliderRef.current?.slickPrev()}
            >
              <MoveLeft size={20} className="dark:text-dotsColor" />
            </button>
            <div className="flex items-center space-x-1">{renderRentalDots()}</div>
            <button
              className="text-typography dark:text-primary"
              onClick={() => rentalSliderRef.current?.slickNext()}
            >
              <MoveRight size={20} className="dark:text-dotsColor" />
            </button>
          </div>
        </div>
      )}

      {/* Sale Properties Section */}
      {hasSaleProperties && (
        <div className="container mx-auto max-md:px-4">
          <div className="flex justify-between items-center mb-5">
            <div>
              <h2 className="text-typography sm:text-h2 text-mobile-h2 font-semibold">
                {dictionary?.propertyDetail?.homePageListing?.propertiesForSale ||
                  'Properties For Sale'}
              </h2>
              <p className="text-typography">
                {dictionary?.propertyDetail?.homePageListing?.findYourDreamProperty ||
                  'Find Your Dream Property For Sale'}
              </p>
            </div>
            <div className="hidden lg:flex space-x-5 rtl:space-x-reverse">
              <button
                className="text-typography"
                onClick={() => saleSliderRef.current?.slickPrev()}
              >
                <MoveLeft size={20} className="dark:text-dotsColor rtl:rotate-180" />
              </button>
              <div className="text-typography flex items-center space-x-1">{renderSaleDots()}</div>
              <button
                className="text-typography"
                onClick={() => saleSliderRef.current?.slickNext()}
              >
                <MoveRight size={20} className="dark:text-dotsColor rtl:rotate-180" />
              </button>
            </div>
          </div>

          <Slider {...saleSliderSettings} className="property-slider" ref={saleSliderRef}>
            {saleProperties.map((property) => (
              <div key={property.id} className="px-2">
                <PropertyCard property={property} dictionary={dictionary} lang={lang} />
              </div>
            ))}
          </Slider>

          {/* BUTTON VIEW ALL SALE PROPERTIES */}
          <div className="flex justify-center my-5">
            <Link
              href={'/properties?listedIn=for_sale&page=1&sorting=createdAt%3Aasc'}
              className="inline-flex items-center gap-2 h-[55px] px-6 bg-primary-300 text-white rounded-xl hover:bg-primary-200 transition-colors"
            >
              {dictionary?.propertyDetail?.agencyDetail?.seeSaleProperties || 'See Sale Properties'}
              <MoveUpRight />
            </Link>
          </div>

          <div className="lg:hidden flex space-x-5 py-5 justify-center">
            <button className="text-typography" onClick={() => saleSliderRef.current?.slickPrev()}>
              <MoveLeft size={20} className="dark:text-dotsColor" />
            </button>
            <div className="text-typography flex items-center space-x-1">{renderSaleDots()}</div>
            <button className="text-typography" onClick={() => saleSliderRef.current?.slickNext()}>
              <MoveRight size={20} className="dark:text-dotsColor" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
